/* eslint-disable react/prop-types */
/* eslint-disable function-paren-newline */
/* eslint-disable implicit-arrow-linebreak */
/* eslint-disable indent */
import React from 'react';
import PropTypes from 'prop-types';
import { sortBy } from 'lodash';
import { EntryManager } from '@folio/stripes/smart-components';
import {
  TitleManager,
  stripesConnect,
  CalloutContext,
} from '@folio/stripes/core';
import { Loading } from '@folio/stripes/components';
import { injectIntl } from 'react-intl';

import SelfCheckoutDeviceDetail from './SelfCheckoutDeviceDetail';
import SelfCheckoutDeviceForm from './SelfCheckoutDeviceForm';
import ErrorBoundary from './ErrorBoundary';

class SelfCheckoutDeviceManager extends React.Component {
  static contextType = CalloutContext;
  static manifest = Object.freeze({
    entries: {
      type: 'okapi',
      records: 'configs',
      path: 'configurations/entries?query=module=="edge-sip2" AND configName="selfCheckoutConfig*"&limit=1000',
      resourceShouldRefresh: true,
      throwErrors: false,
      POST: {
        path: 'configurations/entries',
      },
      PUT: {
        path: 'configurations/entries',
      },
      DELETE: {
        path: 'configurations/entries',
      },
    },
    servicePoints: {
      type: 'okapi',
      records: 'servicepoints',
      path: 'service-points?query=cql.allRecords=1 sortby name&limit=1000',
      accumulate: 'true',
    },
    institutions: {
      type: 'okapi',
      records: 'locinsts',
      path: 'location-units/institutions?query=cql.allRecords=1 sortby name&limit=1000',
      accumulate: 'true',
    },
    campuses: {
      type: 'okapi',
      records: 'loccamps',
      path: 'location-units/campuses?query=cql.allRecords=1 sortby name&limit=1000',
      accumulate: 'true',
    },
    libraries: {
      type: 'okapi',
      records: 'loclibs',
      path: 'location-units/libraries?query=cql.allRecords=1 sortby name&limit=1000',
      accumulate: 'true',
    },
    locations: {
      type: 'okapi',
      records: 'locations',
      path: 'locations?query=cql.allRecords=1 sortby name&limit=1000',
      accumulate: 'true',
    },
  });

  static propTypes = {
    label: PropTypes.node.isRequired,
    resources: PropTypes.shape({
      entries: PropTypes.shape({
        records: PropTypes.arrayOf(PropTypes.object),
        hasLoaded: PropTypes.bool,
      }),
      servicePoints: PropTypes.shape({
        records: PropTypes.arrayOf(PropTypes.object),
        hasLoaded: PropTypes.bool,
      }),
    }).isRequired,
    mutator: PropTypes.shape({
      entries: PropTypes.shape({
        POST: PropTypes.func,
        PUT: PropTypes.func,
        DELETE: PropTypes.func,
      }),
    }).isRequired,
    stripes: PropTypes.shape({
      connect: PropTypes.func.isRequired,
      hasPerm: PropTypes.func.isRequired,
    }),
    intl: PropTypes.object,
  };

  componentDidCatch(error, errorInfo) {
    // Suppress EntryManager forEach errors after successful deletion
    if (error.message && error.message.includes('forEach')) {
      console.warn('EntryManager forEach error suppressed:', error.message);
      return;
    }
    // Re-throw other errors
    throw error;
  }

  parseInitialValues = (values = {}) => {
    // If values already has parsed data (from EntryManager), return it directly
    if (values.libraryName !== undefined && !values.value) {
      return values;
    }

    if (!values.value) {
      return {
        servicePointId:
          values.configName?.replace('selfCheckoutConfig.', '') || '',
        libraryName: '',
        terminalLocation: '',
        checkinOk: false,
        checkoutOk: false,
        acsRenewalPolicy: false,
        timeoutPeriod: 5,
        retriesAllowed: 3,
        id: values.id,
        configName: values.configName,
      };
    }

    try {
      const config = JSON.parse(values.value);
      const servicePointId =
        values.configName?.replace('selfCheckoutConfig.', '') || '';

      return {
        servicePointId,
        libraryName: config.libraryName || '',
        terminalLocation: config.terminalLocation || '',
        checkinOk: config.checkinOk || false,
        checkoutOk: config.checkoutOk || false,
        acsRenewalPolicy: config.acsRenewalPolicy || false,
        timeoutPeriod: config.timeoutPeriod || 5,
        retriesAllowed: config.retriesAllowed || 3,
        id: values.id,
        configName: values.configName,
      };
    } catch (e) {
      return {
        servicePointId:
          values.configName?.replace('selfCheckoutConfig.', '') || '',
        libraryName: '',
        terminalLocation: '',
        checkinOk: false,
        checkoutOk: false,
        acsRenewalPolicy: false,
        timeoutPeriod: 5,
        retriesAllowed: 3,
        id: values.id,
        configName: values.configName,
      };
    }
  };

  onBeforeSave = (values) => {
    const servicePointId = values.servicePointId;
    const configName = `selfCheckoutConfig.${servicePointId}`;
    const isEditing = !!values.id;

    const configValue = {
      libraryName: values.libraryName || '',
      terminalLocation: values.terminalLocation || '',
      checkinOk: values.checkinOk || false,
      checkoutOk: values.checkoutOk || false,
      acsRenewalPolicy: values.acsRenewalPolicy || false,
      timeoutPeriod: parseInt(values.timeoutPeriod, 10) || 5,
      retriesAllowed: parseInt(values.retriesAllowed, 10) || 3,
    };

    // Show success message after save
    setTimeout(() => {
      if (this.context) {
        const messageId = isEditing
          ? 'ui-circulation.settings.selfCheckout.device.updated'
          : 'ui-circulation.settings.selfCheckout.device.created';
        this.context.sendCallout({
          message: this.props.intl.formatMessage({ id: messageId }),
          type: 'success',
        });
      }
    }, 100);

    return {
      module: 'edge-sip2',
      configName,
      enabled: true,
      value: JSON.stringify(configValue),
      ...(values.id && { id: values.id }),
    };
  };

  isEntryInUse = () => {
    // For now, allow all deletions
    // In the future, you might want to check if the device is actively being used
    return false;
  };

  render() {
    const servicePoints =
      (this.props.resources.servicePoints || {}).records || [];
    const entriesResource = this.props.resources.entries || {};
    const entriesRecords = Array.isArray(entriesResource.records)
      ? entriesResource.records
      : [];

    // Show loading state while resources are being fetched
    const isLoading =
      !this.props.resources.entries?.hasLoaded ||
      !this.props.resources.servicePoints?.hasLoaded;

    const entryList =
      entriesRecords.length > 0
        ? sortBy(entriesRecords, ['configName']).map((item) => {
            const parsedValues = this.parseInitialValues(item);
            const servicePointId =
              item.configName?.replace('selfCheckoutConfig.', '') || '';
            const servicePoint = servicePoints.find(
              (sp) => sp.id === servicePointId,
            );

            // Use translated names for display
            const translatedServicePointName = servicePoint?.name
              ? this.props.intl.formatMessage({
                  id: `ui-tenant-settings.servicePoints.name.${servicePoint.name}`,
                  defaultMessage: servicePoint.name,
                })
              : null;

            const entry = {
              ...parsedValues,
              name:
                translatedServicePointName ||
                parsedValues.libraryName ||
                'Unknown Device',
            };

            return entry;
          })
        : [];

    const permissions = {
      put: 'ui-circulation.settings.other-settings',
      post: 'ui-circulation.settings.other-settings',
      delete: 'ui-circulation.settings.other-settings',
    };

    const isEditable = Object.values(permissions).some((p) =>
      this.props.stripes.hasPerm(p),
    );

    if (isLoading) {
      return (
        <TitleManager
          page={this.props.intl.formatMessage({
            id: 'ui-circulation.settings.selfCheckout.devices.title',
          })}
        >
          <Loading />
        </TitleManager>
      );
    }

    return (
      <TitleManager
        page={this.props.intl.formatMessage({
          id: 'ui-circulation.settings.selfCheckout.devices.title',
        })}
      >
        <ErrorBoundary>
          <EntryManager
            {...this.props}
            parentMutator={this.props.mutator}
            parentResources={this.props.resources}
            resourceKey="entries"
            entryList={entryList}
            /* kware start editing */
            dataSource="servicePoints"
            appName="ui-tenant-settings"
            translatableFields={['name']}
            /* kware end editing */
            detailComponent={SelfCheckoutDeviceDetail}
            parseInitialValues={this.parseInitialValues}
            paneTitle={this.props.label}
            entryLabel={this.props.label}
            entryFormComponent={SelfCheckoutDeviceForm}
            nameKey="name"
            editable={isEditable}
            enableDetailsActionMenu
            permissions={permissions}
            onBeforeSave={this.onBeforeSave}
            isEntryInUse={this.isEntryInUse}
            prohibitItemDelete={{
              close: this.props.intl.formatMessage({
                id: 'stripes-core.button.close',
              }),
              label: this.props.intl.formatMessage({
                id: 'ui-circulation.settings.selfCheckout.device.cannotDelete.label',
              }),
              message: this.props.intl.formatMessage({
                id: 'ui-circulation.settings.selfCheckout.device.cannotDelete.message',
              }),
            }}
          />
        </ErrorBoundary>
      </TitleManager>
    );
  }
}

export default stripesConnect(injectIntl(SelfCheckoutDeviceManager));
